'use client';

import { createContext, useContext, useState, ReactNode } from 'react';
import type { BreadcrumbItem } from '@/types/breadcrumbs';

interface BreadcrumbContextType {
  customBreadcrumbs: BreadcrumbItem[] | null;
  hasCustomBreadcrumbs: boolean;
  setCustomBreadcrumbs: (breadcrumbs: BreadcrumbItem[] | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [customBreadcrumbs, setCustomBreadcrumbs] = useState<BreadcrumbItem[] | null>(null);
  const [hasCustomBreadcrumbs, setHasCustomBreadcrumbs] = useState(false);

  const setBreadcrumbs = (breadcrumbs: BreadcrumbItem[] | null) => {
    setCustomBreadcrumbs(breadcrumbs);
    setHasCustomBreadcrumbs(breadcrumbs !== null);
  };

  return (
    <BreadcrumbContext.Provider
      value={{ customBreadcrumbs, hasCustomBreadcrumbs, setCustomBreadcrumbs: setBreadcrumbs }}
    >
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumb() {
  const context = useContext(BreadcrumbContext);
  if (context === undefined) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider');
  }
  return context;
}

/**
 * Like {@link useBreadcrumb} but returns null instead of throwing when no
 * BreadcrumbProvider is mounted. For progressive-enhancement consumers (e.g. the
 * club shell's optional breadcrumb trail) that must degrade gracefully — not
 * crash the surrounding UI — when rendered outside a provider (isolated tests, or
 * a future provider-less layout).
 */
export function useBreadcrumbOptional(): BreadcrumbContextType | null {
  return useContext(BreadcrumbContext) ?? null;
}
